import EventEmitter from "./EventEmitter";
import { addClass, assign, elementsFromHTML, hide, removeClass, show, toggleClass } from "./common";

function isElement(node) {
  return node.nodeType === Node.ELEMENT_NODE;
}

let nextId = 0;

const View = EventEmitter.extend(
  /** @lends View.prototype */
  {
    /**
     * Abstract view class
     * @param {*} model
     * @param {object} opts
     * @constructs
     * @extends EventEmitter
     * @abstract
     */
    constructor(model, opts) {
      EventEmitter.call(this);

      /** @type {*} */
      this.model = model;

      /**
       * @protected
       * @type {object}
       */
      this.opts = assign(this.getDefaultOptions(), opts);

      /**
       * Contains all nodes of generated HTML
       * @protected
       * @type {Node[]}
       */
      this.nodes = [];

      /**
       * Contains all elements in {@link View#nodes}
       * @protected
       * @type {HTMLElement[]}
       */
      this.elements = [];

      /**
       * @protected
       * @type {number}
       */
      this.id = nextId++;

      /**
       * Whether the view is inserted in document
       * @type {boolean}
       */
      this.isMounted = false;
    },

    /**
     * The preferred way of rendering the view's HTML. Establishes {@link View#nodes} and {@link View#elements}
     * @param {string} html
     * @protected
     */
    setHTML(html) {
      this.nodes = elementsFromHTML(html);
      this.elements = this.nodes.filter(isElement);
    },

    /**
     * Default options factory to be overridden by subclasses
     * @protected
     * @returns {object}
     */
    getDefaultOptions() {
      return {};
    },

    /**
     * Clear`display: none` for all elements
     */
    show() {
      this.elements.forEach(show);
    },

    /**
     * Apply `display: none` for all elements
     */
    hide() {
      this.elements.forEach(hide);
    },

    /**
     * Add class for all elements
     * @param {string} className
     */
    addClass(className) {
      this.elements.forEach(el => addClass(el, className));
    },

    /**
     * Remove class for all elements
     * @param {string} className
     */
    removeClass(className) {
      this.elements.forEach(el => removeClass(el, className));
    },

    /**
     * Toggle class for all elements
     * @param {string} className
     * @param {boolean} state
     */
    toggleClass(className, state) {
      this.elements.forEach(el => toggleClass(el, className, state));
    },

    /**
     * Append all nodes to parent
     * @param {Node} parent
     */
    appendTo(parent) {
      this.nodes.forEach(el => { parent.appendChild(el); });
      this.handleMounted();
    },

    /**
     * Insert all nodes before ref
     * @param {Node} ref
     */
    insertBefore(ref) {
      if(this.nodes[this.nodes.length - 1].nextSibling === ref) return;
      this.nodes.forEach(el => { ref.parentNode.insertBefore(el, ref); });
      this.handleMounted();
    },

    /**
     * Insert all nodes before ref
     * @param {View} ref
     */
    insertBeforeView(ref) {
      this.insertBefore(ref.nodes[0]);
    },

    /**
     * Insert all nodes after ref
     * @param {Node} ref
     */
    insertAfter(ref) {
      ref = ref.nextSibling;
      if(ref === this.nodes[0]) return;
      this.insertBefore(ref);
    },

    /**
     * Insert all nodes after ref
     * @param {View} ref
     */
    insertAfterView(ref) {
      const node = ref.nodes[ref.nodes.length - 1];
      this.insertAfter(node);
    },

    /**
     * Apply style to all elements
     * @param {object} decl - style in the form propname: "value"
     */
    css(decl) {
      this.elements.forEach(el => {
        Object.keys(decl).forEach(propName => {
          el.style.setProperty(propName, decl[propName]);
        });
      });
    },

    /**
     * Removes all event listeners as well as nodes from DOM
     */
    destroy() {
      this.nodes.forEach(el => { if(el.parentNode) el.parentNode.removeChild(el); });
      this.stopListening();
      this.emit("destroy");
    },

    /**
     * Gets an element with given id and standard `_<id>` suffix
     * @param {string} id
     * @returns {HTMLElement | null}
     * @protected
     */
    getElement(id) {
      return document.getElementById(`${id}_${this.id}`);
    },

    /**
     * Do work after being inserted into document. It makes sense to bind all DOM events here and using {@link View#getElement}
     * @protected
     */
    onMount() {},

    /**
     * Handles calling {@link View#onMount}
     * @private
     */
    handleMounted() {
      if(this.isMounted) return;
      const el = this.elements[0];
      if(el.ownerDocument.body.contains(el)) {
        this.onMount();
        this.isMounted = true;
      }
    },

    /**
     * Makes the view emit event of type `event` whenever an event of same type is fired on `el`
     * @param {HTMLElement} el
     * @param {string} event
     * @protected
     */
    forwardEvent(el, event, rename) {
      el.addEventListener(event, evt => { this.emit(rename || event, { event: evt }); });
    },

    /**
     * Returns an element used for computing offset
     * @returns {HTMLElement}
     * @protected
     */
    getReferenceElement() {
      return this.elements[0];
    },

    /**
     * Returns an object used in conjunction with {@link View#getPositionDelta} to resolve change in position
     * @returns {*}
     */
    getPositionReference() {
      const el = this.getReferenceElement();
      return [el.offsetLeft, el.offsetTop];
    },

    /**
     * Like {@link View#getPositionReference}, but takes animation in account.
     * @returns {*}
     */
    getPositionReferenceAnimated() {
      const rect = this.getReferenceElement().getBoundingClientRect();
      return [rect.left, rect.top];
    },

    /**
     * Get offset change since calling {@link View#getPositionReference} given it's return value.
     * @param {*} ref - object obtained by calling {@link View#getPositionReference}
     * @returns {number[]} - position change as [dx, dy]
     */
    getPositionDelta([x, y]) {
      const el = this.getReferenceElement();
      return [el.offsetLeft - x, el.offsetTop - y];
    },

    /**
     * Like {@link View#getPositionDelta}, but is dual ti {@link View#getPositionReferenceAnimated};
     * @returns {*}
     */
    getPositionDeltaAnimated([x, y]) {
      const rect = this.getReferenceElement().getBoundingClientRect();
      return [rect.left - x, rect.top - y];
    }
  });

export default View;
